/**
 * "Date range" sync for a wheel zoom or a drag pan: a runtime channel between
 * the charts mounted on one layout.
 *
 * `sync.barRange` fans `setBarWindow` out in the reducer, but that action only
 * carries the *count* — the box, the presets, 🔍±. Where a chart is scrolled to
 * is session state (D8), so a wheel zoom or a pan never reaches the reducer and
 * linked charts drifted apart the moment anybody touched one. charts.md already
 * names the shape for this: a runtime channel between mounted charts, not a
 * contract field, because a pan is a range sixty times a second.
 *
 * **The range is in time** (epoch seconds, the X axis's own units), so two
 * charts on different symbols or intervals line up by date rather than by bar
 * index — a weekly chart beside a daily one shows the same months.
 *
 * **Echo is stopped by equality, not by a flag.** A peer that applies a range
 * fires its own window listener, which publishes the same range back; the
 * channel remembers the last range it carried and drops a repeat. That holds
 * whether SciChart raises the event synchronously or on the next frame, which a
 * flag set around the call would not.
 *
 * Module scope with subscribers, `symbol-index.ts`'s shape: the members are
 * siblings in different subtrees, and nothing about it belongs in React state.
 */

export interface TimeWindow {
  min: number;
  max: number;
}

type Apply = (window: TimeWindow) => void;

const channels = new Map<string, Map<string, Apply>>();
const lastCarried = new Map<string, TimeWindow>();

/** Half a second: the axis is in seconds, and no bar is shorter than one. */
const SAME = 0.5;

function same(a: TimeWindow, b: TimeWindow): boolean {
  return Math.abs(a.min - b.min) < SAME && Math.abs(a.max - b.max) < SAME;
}

/**
 * Join `link` as `member`; `apply` is called with every window another member
 * publishes. Returns the leave function.
 */
export function joinWindowLink(link: string, member: string, apply: Apply): () => void {
  let members = channels.get(link);
  if (!members) channels.set(link, (members = new Map()));
  members.set(member, apply);
  return () => {
    const now = channels.get(link);
    if (now?.get(member) !== apply) return;
    now.delete(member);
    if (now.size === 0) {
      channels.delete(link);
      lastCarried.delete(link);
    }
  };
}

/** Tell every other member of `link` that `member`'s window moved. */
export function publishWindow(link: string, member: string, window: TimeWindow): void {
  if (!(window.max > window.min)) return;
  const members = channels.get(link);
  if (!members?.has(member)) return;
  const prev = lastCarried.get(link);
  if (prev && same(prev, window)) return;
  lastCarried.set(link, { min: window.min, max: window.max });
  for (const [id, apply] of members) if (id !== member) apply(window);
}
